// The First Mile GRN section — completion, escalations and exclusions, board-wide and per
// warehouse, in either of two views the user toggles between (SaaS-style segmented control):
//
//   Blocks — a headline pair (GRN Marked / Escalations + Exclusions) then one card per warehouse,
//            each carrying its OWN escalation + exclusion tiles.
//   Table  — one row per warehouse plus a Total row; columns GRN Marked | Escalations | Exclusions.
//
// Both views read the same per-dock cells from /first-mile-escalations/ and both drill down through
// the same handler, so they can never disagree. Every figure shows two completion rates: the raw
// one (marked / total) and the SLA-adjusted one, which takes the exclusions out of BOTH sides —
// GRNs that could never fairly meet a same-day SLA (raised after 6 PM, heavy, oversized tile).
import { Card, Col, Row, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useState } from "react";
import styles from "../index.module.scss";
import {
  EscalationDrilldownKey,
  FirstMileEscalationsResponse,
  FirstMileGrnCell,
} from "../control_tower.api";
import {
  ESCALATION_DRILLDOWN_LABEL,
  EXCLUSION_ORDER,
  EXCLUSION_SHORT,
  FIRST_MILE_DOCK_LABEL,
  FIRST_MILE_WAREHOUSES,
  grnRates,
} from "../first_mile_meta";
import { FirstMileWarehouseKey } from "../types";
import SectionViewToggle, { ViewMode } from "./section_view_toggle";

type Drill = (bucket: EscalationDrilldownKey, dock?: FirstMileWarehouseKey) => void;

interface Props {
  loading: boolean;
  data: FirstMileEscalationsResponse | null;
  onDrill: Drill;
}

interface RowData {
  key: string;
  dock?: FirstMileWarehouseKey; // undefined on the Total row
  label: string;
  cell?: FirstMileGrnCell;
}

// "18 of 48 marked" + the adjusted rate beneath. Shared by the headline card and each warehouse card.
const CompletionBody: React.FC<{ cell?: FirstMileGrnCell }> = ({ cell }) => {
  const r = grnRates(cell);
  return (
    <>
      <div className={styles.grnPercent}>{r.rawPct}%</div>
      <div className={styles.grnSub}>
        {r.marked} of {r.total} marked
      </div>
      <Tooltip title="Excludes GRNs that can't fairly meet a same-day SLA (raised after 6 PM, heavy, oversized tile) from both sides of the ratio.">
        <div className={styles.adjLine}>
          adj {r.adjustedPct}% · {r.eligibleMarked} of {r.eligible} eligible
        </div>
      </Tooltip>
    </>
  );
};

// The four drillable figures on a cell: the raised escalation + the three exclusions.
const tilesFor = (cell?: FirstMileGrnCell) => [
  { key: "vendor_to_send" as EscalationDrilldownKey, label: "Vendor to Send", value: cell?.escalations ?? 0 },
  ...EXCLUSION_ORDER.map((k) => ({
    key: k as EscalationDrilldownKey,
    label: EXCLUSION_SHORT[k],
    value: cell?.exclusions?.[k] ?? 0,
  })),
];

const FirstMileGrnSection: React.FC<Props> = ({ loading, data, onDrill }) => {
  const [view, setView] = useState<ViewMode>("blocks");
  const totals = data?.totals;
  const byWh = data?.by_warehouse;

  const rows: RowData[] = [
    ...FIRST_MILE_WAREHOUSES.map((w) => ({
      key: w,
      dock: w,
      label: FIRST_MILE_DOCK_LABEL[w],
      cell: byWh?.[w],
    })),
    { key: "__total", label: "Total", cell: totals },
  ];

  const columns: ColumnsType<RowData> = [
    {
      title: "Warehouse",
      dataIndex: "label",
      key: "label",
      render: (v: string, r) => <strong style={{ color: r.dock ? undefined : "#262626" }}>{v}</strong>,
    },
    {
      title: "GRN Marked",
      key: "marked",
      render: (_, r) => {
        const g = grnRates(r.cell);
        return (
          <div>
            <span style={{ fontSize: 18, fontWeight: 700, color: "#389e0d" }}>{g.rawPct}%</span>{" "}
            <span className={styles.cellMuted}>
              {g.marked} of {g.total}
            </span>
            <div className={styles.cellMuted}>
              adj {g.adjustedPct}% · {g.eligibleMarked} of {g.eligible} eligible
            </div>
          </div>
        );
      },
    },
    {
      title: "Escalations",
      key: "escalations",
      render: (_, r) => (
        <Tooltip title="Procurement issues raised by hand (vendor to send, damaged material, …)">
          <span className={styles.cellLink} onClick={() => onDrill("vendor_to_send", r.dock)}>
            {r.cell?.escalations ?? 0}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Exclusions",
      key: "exclusions",
      render: (_, r) => (
        <div>
          <Tooltip title="GRNs excluded from the adjusted SLA (any reason)">
            <span className={styles.cellLink} onClick={() => onDrill("excluded", r.dock)}>
              {r.cell?.excluded ?? 0}
            </span>
          </Tooltip>
          <div className={styles.cellMuted} style={{ display: "flex", gap: 10, marginTop: 2 }}>
            {EXCLUSION_ORDER.map((k) => (
              <span key={k} className={styles.cellLink} style={{ fontWeight: 400, color: "#8c8c8c" }}
                    onClick={() => onDrill(k, r.dock)}>
                {EXCLUSION_SHORT[k]} {r.cell?.exclusions?.[k] ?? 0}
              </span>
            ))}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      <div className={styles.sectionHead}>
        <span className={styles.sectionHeadTitle}>GRN</span>
        <SectionViewToggle value={view} onChange={setView} />
      </div>

      {view === "table" ? (
        <Table
          rowKey="key"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={false}
          rowClassName={(r) => (r.dock ? "" : "ant-table-row-selected")}
        />
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} lg={8}>
              <Tooltip title="GRNs marked (received) out of all due in this dispatch window. Click to list them.">
                <Card
                  size="small"
                  loading={loading}
                  className={styles.overallCard}
                  style={{ borderLeft: "3px solid #52c41a", height: "100%" }}
                  onClick={() => onDrill("all")}
                >
                  <div className={styles.blockTitle}>GRN Marked</div>
                  <CompletionBody cell={totals} />
                </Card>
              </Tooltip>
            </Col>

            <Col xs={24} lg={16}>
              <Card
                size="small"
                loading={loading}
                style={{ border: "1px solid #e6e8ec", borderLeft: "3px solid #f5222d", borderRadius: 10, height: "100%" }}
              >
                <div className={styles.escHeader}>
                  <span className={styles.blockTitle} style={{ marginBottom: 0 }}>
                    Escalations &amp; Exclusions
                  </span>
                  <Tooltip title="Everything flagged on the GRN board in this window. Click for the full list.">
                    <span className={styles.escTotal} onClick={() => onDrill("all")}>
                      {(totals?.escalations ?? 0) + (totals?.excluded ?? 0)}
                    </span>
                  </Tooltip>
                </div>
                <div className={styles.escGrid}>
                  {tilesFor(totals).map((t) => (
                    <Tooltip key={t.key} title={ESCALATION_DRILLDOWN_LABEL[t.key]}>
                      <div className={styles.escBucket} onClick={() => onDrill(t.key)}>
                        <div className={styles.escBucketValue}>{t.value}</div>
                        <div className={styles.escBucketLabel}>{t.label}</div>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>

          <div className={styles.blockTitle} style={{ marginBottom: 10 }}>
            By Warehouse
          </div>
          <Row gutter={16}>
            {FIRST_MILE_WAREHOUSES.map((w) => (
              <Col key={w} xs={24} lg={8}>
                <Card size="small" loading={loading} className={styles.whCard}>
                  <div className={styles.whCardName}>{FIRST_MILE_DOCK_LABEL[w]}</div>
                  <CompletionBody cell={byWh?.[w]} />
                  <div className={styles.whTileRow}>
                    {tilesFor(byWh?.[w]).map((t) => (
                      <Tooltip key={t.key} title={`${FIRST_MILE_DOCK_LABEL[w]} — ${ESCALATION_DRILLDOWN_LABEL[t.key]}`}>
                        <div className={styles.whTile} onClick={() => onDrill(t.key, w)}>
                          <div className={styles.whTileValue}>{t.value}</div>
                          <div className={styles.whTileLabel}>{t.label}</div>
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}
    </div>
  );
};

export default FirstMileGrnSection;
